import {
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import {
	Form,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { PlatformIcon } from "./PlatformIcon"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import { TIntegration, TNewIntegrationSchema } from "./store"
import * as Validator from "yup"
import { Optional } from "utility-types"
import { useState } from "react"
import tataTeleImage1 from "./SetupImages/tatatele-image-1.webp"
import tataTeleImage2 from "./SetupImages/tatatele-image-2.webp"
import tataTeleImage3 from "./SetupImages/tatatele-image-3.webp"
import tataTeleImage4 from "./SetupImages/tatatele-image-4.webp"
import tataTeleImage5 from "./SetupImages/tatatele-image-5.webp"
import tataTeleImage6 from "./SetupImages/tatatele-image-6.webp"

export function TeleCallingIntegratedApps({ data }: { data: TIntegration }) {
	switch (data.platform) {
		case "tc_knowlarity":
			return <KnowlarityIntegratedApp data={data} />
		case "tc_tatatele":
			return <TataTeleIntegratedApp data={data} />
		case "tc_callerdesk":
			return <CallerDeskIntegratedApp data={data} />
		default:
			return null
	}
}

function KnowlarityIntegratedApp({ data }: { data: TIntegration }) {
	return (
		<Stack gap="4">
			<Inline gap="4" alignItems="center">
				<PlatformIcon platform={data.platform} size={"32"} />
				<Stack>
					<Heading as="h4">Knowlarity</Heading>
					<Text color="muted">
						Receive leads directly from Knowlarity into Dashboard
					</Text>
				</Stack>
			</Inline>
			<Text>
				Please follow the instructions from{" "}
				<Text
					as="a"
					href={"https://developer.knowlarity.com"}
					target="_blank"
					rel="noopener noreferrer"
					color="accent"
				>
					official guidelines <Icons.ArrowTopRightOnSquare />
				</Text>
				on how to setup Webhook for Knowlarity.
			</Text>

			<Inline gap="4" flexWrap="wrap">
				<CopyToClipboard<HTMLDivElement>>
					{({ nodeToCopy, copyNodeTextToClipboard }) => (
						<Stack gap="1">
							<Text fontWeight="semibold">Webhook URL</Text>
							<Inline
								gap="2"
								alignItems="center"
								paddingY="1"
								paddingLeft="4"
								paddingRight="1"
								borderWidth="1"
								rounded="md"
								bgColor="subtle"
							>
								<Box ref={nodeToCopy}>
									{data.telecalling_webhook_config?.url}
								</Box>
								<CopyToClipboardButton
									size="sm"
									onClick={async () => {
										return copyNodeTextToClipboard()
									}}
								>
									<Icons.ClipboardCopy />
								</CopyToClipboardButton>
							</Inline>
						</Stack>
					)}
				</CopyToClipboard>
				<CopyToClipboard<HTMLDivElement>>
					{({ nodeToCopy, copyNodeTextToClipboard }) => (
						<Stack gap="1">
							<Text fontWeight="semibold">Key</Text>
							<Inline
								gap="2"
								alignItems="center"
								paddingY="1"
								paddingLeft="4"
								paddingRight="1"
								borderWidth="1"
								rounded="md"
								bgColor="subtle"
							>
								<Box ref={nodeToCopy}>
									{data.telecalling_webhook_config?.key}
								</Box>
								<CopyToClipboardButton
									size="sm"
									onClick={async () => {
										return copyNodeTextToClipboard()
									}}
								>
									<Icons.ClipboardCopy />
								</CopyToClipboardButton>
							</Inline>
						</Stack>
					)}
				</CopyToClipboard>
			</Inline>
		</Stack>
	)
}
function TataTeleIntegratedApp({ data }: { data: TIntegration }) {
	return (
		<Grid>
			<Col sm={12} md={8}>
				<Box borderWidth={"1"} bgColor={"default"} rounded="md" padding="4">
					<Stack gap="4">
						<Inline gap="4" alignItems="center">
							<PlatformIcon platform={data.platform} size={"48"} />
							<Stack>
								<Heading as="h4">Tata Tele Business Communication</Heading>
								<Text color="muted">
									Receive leads directly from Tata Tele Business into Dashboard
								</Text>
							</Stack>
						</Inline>
						<Inline
							gap="2"
							bgColor={"accent"}
							borderWidth={"1"}
							alignItems="center"
							padding={"4"}
							rounded={"md"}
						>
							<Icons.Info size={"6"} color="accent" />
							<Text>
								Follow the instructions from{" "}
								<Text
									as="a"
									color="accent"
									href={
										"https://docs.smartflo.tatatelebusiness.com/reference/introduction-to-apis-1"
									}
									target="_blank"
									rel="noopener noreferrer"
									textDecoration={"underline"}
								>
									official guidelines <Icons.ArrowTopRightOnSquare />
								</Text>{" "}
								on how to setup Webhook for Tata Tele Business Communication.
							</Text>
						</Inline>

						<Stack gap="6">
							<Stack gap="4">
								<Stack gap="2">
									<Heading as="h2">Open Webhooks in Tata Tele</Heading>
									<Text>
										Log in at{" "}
										<Text
											as="a"
											href="https://cloudphone.tatateleservices.com/"
											target="_blank"
											rel="noopener noreferrer"
											color="accent"
										>
											https://cloudphone.tatateleservices.com{" "}
											<Icons.ArrowTopRightOnSquare />
										</Text>{" "}
										click <strong>API Connect</strong> in the left sidebar →
										click <strong>Webhook</strong>.
									</Text>
								</Stack>
								<Box bgColor={"default"} rounded="lg" overflow="hidden">
									<img
										src={tataTeleImage1}
										alt="Tata Tele portal — API Connect sidebar"
										loading="lazy"
									/>
								</Box>
								<Box bgColor={"default"} rounded="lg" overflow="hidden">
									<img
										src={tataTeleImage2}
										alt="Tata Tele portal — Webhook page"
										loading="lazy"
									/>
								</Box>
							</Stack>
							<Divider marginY={"0"} />
							<Stack gap="4">
								<Stack gap="2">
									<Heading as="h3">
										Connection 1 - Incoming call answered
									</Heading>
									<Text>
										Click + <strong>Add Webhook</strong> and set:
									</Text>
								</Stack>
								<Stack as="ul" listStyleType="disc" gap="2" paddingLeft="4">
									<Box as="li">
										<strong>Name:</strong> Sembark IBD Call answered by Agent
									</Box>
									<Box as="li">
										<strong>Method:</strong> POST → paste the{" "}
										<strong>Webhook URL</strong>
									</Box>
									<Box as="li">
										<strong>Channel:</strong> Voice
									</Box>
									<Box as="li">
										<strong>Call Direction:</strong> Inbound
									</Box>
									<Box as="li">
										<strong>Trigger:</strong> Call answered by Agent
									</Box>
									<Box as="li">
										<strong>My Numbers:</strong> All numbers selected
									</Box>
									<Box as="li">
										<strong>Date Time Format:</strong> ISO 8601
									</Box>
									<Box as="li">
										<strong>Timezone:</strong> UTC
									</Box>
									<Box as="li">
										<strong>Retry Webhook:</strong> Enabled
									</Box>
									<Box as="li">
										<strong>Content Type:</strong> JSON{" "}
										<em>(leave the code box as it is)</em>
									</Box>
								</Stack>
								<Box rounded="lg" overflow="hidden">
									<img
										src={tataTeleImage6}
										alt="Add Webhook form — Connection 1"
										loading="lazy"
									/>
								</Box>
								<Text>
									Then open the <strong>Headers</strong> tab →{" "}
									<strong>+ Add</strong> → Key: <code>X-Api-Key</code>, Value:
									your Sembark <strong>Key</strong>. Type it exactly — capitals
									and dashes matter.
								</Text>
								<Box rounded="lg" overflow="hidden">
									<img
										src={tataTeleImage5}
										alt="Headers tab — X-Api-Key"
										loading="lazy"
									/>
								</Box>
								<Text>
									<strong>Save.</strong>
								</Text>
							</Stack>
							<Divider marginY={"0"} />
							<Stack gap="4">
								<Stack gap="2">
									<Heading as="h3">Connection 2 - Incoming call missed</Heading>
									<Text>
										Click + <strong>Add Webhook</strong> and set:
									</Text>
								</Stack>
								<Stack as="ul" listStyleType="disc" gap="2" paddingLeft="4">
									<Box as="li">
										<strong>Name:</strong> Sembark IBD Call hangup missed
									</Box>
									<Box as="li">
										<strong>Method:</strong> POST → paste the{" "}
										<strong>Webhook URL</strong>
									</Box>
									<Box as="li">
										<strong>Channel:</strong> Voice
									</Box>
									<Box as="li">
										<strong>Call Direction:</strong> Inbound
									</Box>
									<Box as="li">
										<strong>Trigger:</strong> Call hangup (Missed or Answered)
									</Box>
									<Box as="li">
										<strong>My Numbers:</strong> All numbers selected
									</Box>
									<Box as="li">
										<strong>Date Time Format:</strong> ISO 8601
									</Box>
									<Box as="li">
										<strong>Timezone:</strong> UTC
									</Box>
									<Box as="li">
										<strong>Retry Webhook:</strong> Enabled
									</Box>
									<Box as="li">
										<strong>Content Type:</strong> JSON{" "}
										<em>(leave the code box as it is)</em>
									</Box>
								</Stack>
								<Box rounded="lg" overflow="hidden">
									<img
										src={tataTeleImage4}
										alt="Add Webhook form - Connection 2"
										loading="lazy"
									/>
								</Box>
								<Text>
									Add the same <code>X-Api-Key</code> header.{" "}
									<strong>Save.</strong>
								</Text>
								<Box rounded="lg" overflow="hidden">
									<img
										src={tataTeleImage5}
										alt="Headers tab — X-Api-Key"
										loading="lazy"
									/>
								</Box>
								<Text color="muted">
									<em>
										This one covers missed calls too — there&apos;s no separate
										missed-call connection.
									</em>
								</Text>
							</Stack>
							<Divider marginY={"0"} />
							<Stack gap="4">
								<Stack gap="2">
									<Heading as="h3">Connection 3 - Outgoing calls</Heading>
									<Text>
										Click + <strong>Add Webhook</strong> and set:
									</Text>
								</Stack>
								<Stack gap="2" as="ul" listStyleType="disc" paddingLeft="4">
									<Box as="li">
										<strong>Name:</strong> Sembark OBD Call hangup missed or
										answered
									</Box>
									<Box as="li">
										<strong>Method:</strong> POST → same{" "}
										<strong>Webhook URL</strong>
									</Box>
									<Box as="li">
										<strong>Channel:</strong> Voice
									</Box>
									<Box as="li">
										<strong>Call Direction:</strong> Outbound
									</Box>
									<Box as="li">
										<strong>Call Type:</strong> tick all three — Click to call,
										Call broadcast, Dialer
									</Box>
									<Box as="li">
										<strong>Trigger:</strong> Call hangup (Missed or Answered)
									</Box>
									<Box as="li">
										<strong>My Numbers:</strong> All numbers selected
									</Box>
									<Box as="li">
										<strong>Date Time Format:</strong> ISO 8601
									</Box>
									<Box as="li">
										<strong>Timezone:</strong> UTC
									</Box>
									<Box as="li">
										<strong>Retry Webhook:</strong> Enabled
									</Box>
									<Box as="li">
										<strong>Content Type:</strong> JSON
									</Box>
								</Stack>
								<Box rounded="lg" overflow="hidden">
									<img
										src={tataTeleImage3}
										alt="Add Webhook form — Connection 3"
										loading="lazy"
									/>
								</Box>
								<Text>
									Add the <code>X-Api-Key</code> header. <strong>Save.</strong>
								</Text>
								<Box rounded="lg" overflow="hidden">
									<img
										src={tataTeleImage5}
										alt="Headers tab — X-Api-Key"
										loading="lazy"
									/>
								</Box>
								<Text color="muted">
									<em>
										The three Call Type boxes only appear after you pick
										Outbound. Miss one and those calls won&apos;t log.
									</em>
								</Text>
							</Stack>
							<Divider marginY={"0"} />
							<Stack gap="2">
								<Heading as="h3">Check it works</Heading>
								<Text>
									All three should show <strong>Enabled</strong> in your webhook
									list. Then make a test call and confirm it appears in Sembark
									within a few seconds.
								</Text>
							</Stack>
						</Stack>
					</Stack>
				</Box>
			</Col>
			<Col sm={12} md={4}>
				<Box
					borderWidth="1"
					bgColor={"default"}
					rounded="md"
					padding="4"
					style={{ position: "sticky", top: "1rem" }}
				>
					<Stack gap="4">
						<Stack>
							<Heading as="h5">Webhook Configuration</Heading>
							<Text color="muted">
								Paste both into Tata Tele. The same URL and X-Api-Key work for
								all three webhooks.
							</Text>
						</Stack>
						<CopyToClipboard<HTMLDivElement>>
							{({ nodeToCopy, copyNodeTextToClipboard }) => (
								<Stack gap="1">
									<Text fontWeight="semibold" fontSize="sm">
										Webhook URL
									</Text>
									<Inline
										gap="2"
										alignItems="center"
										paddingY="1"
										paddingLeft="4"
										paddingRight="1"
										borderWidth="1"
										rounded="md"
										bgColor="inset"
									>
										<Box
											ref={nodeToCopy}
											fontFamily="mono"
											overflowX="hidden"
											whiteSpace="nowrap"
											flex="1"
										>
											{data.telecalling_webhook_config?.url}
										</Box>
										<CopyToClipboardButton
											size="sm"
											onClick={async () => {
												return copyNodeTextToClipboard()
											}}
										>
											<Icons.ClipboardCopy />
										</CopyToClipboardButton>
									</Inline>
								</Stack>
							)}
						</CopyToClipboard>
						<CopyToClipboard<HTMLDivElement>>
							{({ nodeToCopy, copyNodeTextToClipboard }) => (
								<Stack gap="1">
									<Text fontWeight="semibold" fontSize="sm">
										X-Api-Key
									</Text>
									<Inline
										bgColor={"inset"}
										gap="2"
										alignItems="center"
										paddingY="1"
										paddingLeft="4"
										paddingRight="1"
										borderWidth="1"
										rounded="md"
									>
										<Box
											ref={nodeToCopy}
											overflowX="auto"
											whiteSpace="nowrap"
											flex={"1"}
											fontFamily="mono"
										>
											{data.telecalling_webhook_config?.key}
										</Box>
										<CopyToClipboardButton
											size="sm"
											onClick={async () => {
												return copyNodeTextToClipboard()
											}}
										>
											<Icons.ClipboardCopy />
										</CopyToClipboardButton>
									</Inline>
								</Stack>
							)}
						</CopyToClipboard>
					</Stack>
				</Box>
			</Col>
		</Grid>
	)
}

function CallerDeskIntegratedApp({ data }: { data: TIntegration }) {
	return (
		<Stack gap="4">
			<Inline gap="4" alignItems="center">
				<PlatformIcon platform={data.platform} size={"32"} />
				<Stack>
					<Heading as="h4">Caller Desk</Heading>
					<Text color="muted">
						Receive leads directly from Caller Desk into Dashboard
					</Text>
				</Stack>
			</Inline>
			<Text>
				Please follow the instructions from{" "}
				<Text
					as="a"
					href={"https://api.callerdesk.io/"}
					target="_blank"
					rel="noopener noreferrer"
					color="accent"
				>
					official guidelines <Icons.ArrowTopRightOnSquare />
				</Text>{" "}
				on how to setup Webhook for Caller Desk.
			</Text>

			<Inline gap="4" flexWrap="wrap">
				<CopyToClipboard<HTMLDivElement>>
					{({ nodeToCopy, copyNodeTextToClipboard }) => (
						<Stack gap="1">
							<Text fontWeight="semibold">Webhook URL</Text>
							<Inline
								gap="2"
								alignItems="center"
								paddingY="1"
								paddingLeft="4"
								paddingRight="1"
								borderWidth="1"
								rounded="md"
								bgColor="subtle"
							>
								<Box ref={nodeToCopy}>
									{data.telecalling_webhook_config?.url}
								</Box>
								<CopyToClipboardButton
									size="sm"
									onClick={async () => {
										return copyNodeTextToClipboard()
									}}
								>
									<Icons.ClipboardCopy />
								</CopyToClipboardButton>
							</Inline>
						</Stack>
					)}
				</CopyToClipboard>
				<CopyToClipboard<HTMLDivElement>>
					{({ nodeToCopy, copyNodeTextToClipboard }) => (
						<Stack gap="1">
							<Text fontWeight="semibold">Key</Text>
							<Inline
								gap="2"
								alignItems="center"
								paddingY="1"
								paddingLeft="4"
								paddingRight="1"
								borderWidth="1"
								rounded="md"
								bgColor="subtle"
							>
								<Box ref={nodeToCopy}>
									{data.telecalling_webhook_config?.key}
								</Box>
								<CopyToClipboardButton
									size="sm"
									onClick={async () => {
										return copyNodeTextToClipboard()
									}}
								>
									<Icons.ClipboardCopy />
								</CopyToClipboardButton>
							</Inline>
						</Stack>
					)}
				</CopyToClipboard>
			</Inline>
		</Stack>
	)
}

type TNewKnowlarityIntegrationSchema = TNewIntegrationSchema & {
	knowlarity: {
		x_api_key: string
		authorization: string
	}
}
type TNewTataTeleIntegrationSchema = TNewIntegrationSchema & {
	tatatele: {
		login_id: string
		password: string
	}
}
type TNewCallerDeskIntegrationSchema = TNewIntegrationSchema & {
	callerdesk: {
		auth_code: string
	}
}

const newKnowlarityIntegrationSchemaValidator = Validator.object().shape({
	name: Validator.string()
		.required("Please give a name to your integration")
		.max(50, "Please use 50 or fewer characters for the name"),
	description: Validator.string()
		.required("Please provide an description for this integration")
		.max(500, "Please use 500 or fewer characters for the description"),
	knowlarity: Validator.object().shape({
		x_api_key: Validator.string().required("Please provide your API key"),
		authorization: Validator.string().required(
			"Please provide your authorization token"
		),
	}),
})

export function NewKnowlarityIntegrationForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
}: {
	initialValues?: Optional<TNewKnowlarityIntegrationSchema>
	onSubmit: (data: TNewKnowlarityIntegrationSchema) => Promise<unknown>
	onCancel: () => void
}) {
	const [newIntegrationInitialValues] =
		useState<TNewKnowlarityIntegrationSchema>(() => ({
			name: propInitialValues?.name || "",
			description: propInitialValues?.description || "",
			abilities: propInitialValues?.abilities || [],
			platform: "tc_knowlarity",
			knowlarity: propInitialValues?.knowlarity || {
				x_api_key: "",
				authorization: "",
			},
		}))
	return (
		<Form<TNewKnowlarityIntegrationSchema>
			initialValues={newIntegrationInitialValues}
			validate={validateFormValues(newKnowlarityIntegrationSchemaValidator)}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid gap="6">
							<Col sm={12} md={6}>
								<Inline gap="4">
									<Box>
										<PlatformIcon platform="tc_knowlarity" size="60" />
									</Box>
									<Heading>Credentials</Heading>
								</Inline>
								<Text>
									Enter the API credentials from your Knowlarity account to
									establish the connection.
								</Text>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										label="X API Key"
										name="knowlarity.x_api_key"
										type="text"
										required
										autoComplete="false"
										placeholder="e.g. eyJhbGciOiJIUzI1NiIsInR...."
									/>
									<TextInputField
										label="Authorization Token"
										name="knowlarity.authorization"
										type="text"
										required
										autoComplete="false"
										placeholder="e.g. eyJhbGciOiJIUnR5cCI6IkpXVCJ9...."
									/>
								</Stack>
							</Col>
						</Grid>
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={6}>
								<Stack gap="2">
									<Heading>Integration Details</Heading>
									<Text>
										Add a name and description to identify this
										integration.{" "}
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										label="Name"
										name="name"
										type="text"
										required
										placeholder="e.g. Knowlarity Head Office"
									/>
									<TextInputField
										label="Description"
										name="description"
										type="text"
										required
										placeholder="e.g. Automate enquiry creation by bringing leads from our telecalling provider site into Sembark."
										help="A brief description to help identify this integration."
									/>
								</Stack>
							</Col>
						</Grid>
						<SubmissionError />
						<Divider sm />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Saving..." : "Save Details"}
							</Button>
							<Button disabled={submitting} onClick={onCancel}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

const newTataTeleIntegrationSchemaValidator = Validator.object().shape({
	name: Validator.string()
		.required("Please give a name to your integration")
		.max(50, "Please use 50 or fewer characters for the name"),
	description: Validator.string()
		.required("Please provide an description for this integration")
		.max(500, "Please use 500 or fewer characters for the description"),
	tatatele: Validator.object().shape({
		login_id: Validator.string().required("Please provide your login id"),
		password: Validator.string().required("Please enter your password"),
	}),
})

const editTataTeleIntegrationSchemaValidator = Validator.object().shape({
	tatatele: Validator.object().shape({
		login_id: Validator.string().required("Please provide your login id"),
		password: Validator.string().required("Please enter your password"),
	}),
})
export function NewTataTeleIntegrationForm({
	onSubmit,
	onCancel,
	onEdit,
	initialValues: propInitialValues,
}: {
	initialValues?: Optional<TNewTataTeleIntegrationSchema>
	onEdit?: boolean
	onSubmit: (data: TNewTataTeleIntegrationSchema) => Promise<unknown>
	onCancel: () => void
}) {
	const [newIntegrationInitialValues] = useState<TNewTataTeleIntegrationSchema>(
		() => ({
			name: propInitialValues?.name || "",
			description: propInitialValues?.description || "",
			abilities: propInitialValues?.abilities || [],
			platform: "tc_tatatele",
			tatatele: propInitialValues?.tatatele || {
				login_id: "",
				password: "",
			},
		})
	)

	return (
		<Form<TNewTataTeleIntegrationSchema>
			initialValues={newIntegrationInitialValues}
			validate={validateFormValues(
				onEdit
					? editTataTeleIntegrationSchemaValidator
					: newTataTeleIntegrationSchemaValidator
			)}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid gap="6">
							<Col sm={12} md={6}>
								<Inline gap="4">
									<Box>
										<PlatformIcon platform="tc_tatatele" size="60" />
									</Box>
									<Heading>Credentials</Heading>
								</Inline>
								<Text>
									Enter the API credentials from your Tata Tele Business account
									to establish the connection.
								</Text>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										label="Login ID"
										name="tatatele.login_id"
										type="text"
										required
										autoComplete="false"
										placeholder="e.g. Sembark@123"
									/>
									<TextInputField
										label="Password"
										name="tatatele.password"
										type="password"
										required
										autoComplete="false"
										placeholder="**************"
									/>
								</Stack>
							</Col>
						</Grid>
						{!onEdit ? (
							<>
								<Divider />
								<Grid gap="6">
									<Col sm={12} md={6}>
										<Stack gap="2">
											<Heading>Integration Details</Heading>
											<Text>
												Add a name and description to identify this integration.
											</Text>
										</Stack>
									</Col>
									<Col>
										<Stack gap="4">
											<TextInputField
												label="Name"
												name="name"
												type="text"
												required
												placeholder="e.g. Tata Tele Head Office"
											/>
											<TextInputField
												label="Description"
												name="description"
												type="text"
												required
												placeholder="e.g. Automate enquiry creation by bringing leads from our telecalling provider site into Sembark."
												help="A brief description to help identify this integration."
											/>
										</Stack>
									</Col>
								</Grid>
							</>
						) : null}
						<SubmissionError />
						<Divider sm />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Saving..." : "Save Details"}
							</Button>
							<Button disabled={submitting} onClick={onCancel}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

const newCallerDeskIntegrationSchemaValidator = Validator.object().shape({
	name: Validator.string()
		.required("Please give a name to your integration")
		.max(50, "Please use 50 or fewer characters for the name"),
	description: Validator.string()
		.required("Please provide an description for this integration")
		.max(500, "Please use 500 or fewer characters for the description"),
	callerdesk: Validator.object().shape({
		auth_code: Validator.string().required("Please provide your auth code"),
	}),
})

export function NewCallerDeskIntegrationForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
}: {
	initialValues?: Optional<TNewCallerDeskIntegrationSchema>
	onSubmit: (data: TNewCallerDeskIntegrationSchema) => Promise<unknown>
	onCancel: () => void
}) {
	const [newIntegrationInitialValues] =
		useState<TNewCallerDeskIntegrationSchema>(() => ({
			name: propInitialValues?.name || "",
			description: propInitialValues?.description || "",
			abilities: propInitialValues?.abilities || [],
			platform: "tc_callerdesk",
			callerdesk: propInitialValues?.callerdesk || {
				auth_code: "",
			},
		}))
	return (
		<Form<TNewCallerDeskIntegrationSchema>
			initialValues={newIntegrationInitialValues}
			validate={validateFormValues(newCallerDeskIntegrationSchemaValidator)}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid gap="6">
							<Col sm={12} md={6}>
								<Inline gap="4">
									<Box>
										<PlatformIcon platform="tc_callerdesk" size="60" />
									</Box>
									<Heading>Credentials</Heading>
								</Inline>
								<Text>
									Enter the API credentials from your Caller Desk account to
									establish the connection.
								</Text>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										label="Auth Code"
										name="callerdesk.auth_code"
										type="text"
										required
										autoComplete="false"
										placeholder="e.g. eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...."
									/>
								</Stack>
							</Col>
						</Grid>
						<Divider />
						<Grid gap="6">
							<Col sm={12} md={6}>
								<Stack gap="2">
									<Heading>Integration Details</Heading>
									<Text>
										Add a name and description to identify this
										integration.{" "}
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										label="Name"
										name="name"
										type="text"
										required
										placeholder="e.g. Caller Desk Head Office"
									/>
									<TextInputField
										label="Description"
										name="description"
										type="text"
										required
										placeholder="e.g. Automate enquiry creation by bringing leads from our telecalling provider site into Sembark."
										help="A brief description to help identify this integration."
									/>
								</Stack>
							</Col>
						</Grid>
						<SubmissionError />
						<Divider sm />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Saving..." : "Save Details"}
							</Button>
							<Button disabled={submitting} onClick={onCancel}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}
